import { AppBarLoader } from '@components/AppBar/app-bar-loader';
import { BAD_REQUEST_STATUS_CODE, EMPTY_STRING } from '@constants';
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { RootState } from '@store/store';

const appBarLoader = new AppBarLoader();

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL,
  prepareHeaders: async (headers, { getState }) => {
    if (!(getState() as RootState).user.isParticipantMode) {
      const authToken = await appBarLoader.getAccessToken();
      const idToken = authToken ? authToken.idToken : EMPTY_STRING;

      const skipContentType = headers.get('X-Skip-Content-Type') === 'true';
      headers.delete('X-Skip-Content-Type');

      if (idToken) {
        headers.set('Authorization', `Bearer ${idToken}`);
      }

      if (!skipContentType) {
        headers.set('Content-Type', 'application/json');
      }
      headers.set('Accept', 'application/json');

      return headers;
    }
  },
});

interface CustomFetchArgs extends FetchArgs {
  isInterviewRequest?: boolean;
}

const dynamicBaseQuery: BaseQueryFn<CustomFetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const participantToken = (api.getState() as RootState).user.participantToken;
  const selectedInterview = (api.getState() as RootState).interview.selectedInterview;
  let adjustedUrl = args.url;

  if (args.isInterviewRequest) {
    if (!selectedInterview && !participantToken) {
      return {
        error: {
          status: BAD_REQUEST_STATUS_CODE,
          statusText: 'Bad Request',
          data: 'No selected interview',
        },
      };
    }

    if (participantToken) {
      adjustedUrl = `survey/${participantToken}/${args.url}`;
    }

    if (selectedInterview?.businessId && selectedInterview?.worksheetId) {
      const { businessId, worksheetId } = selectedInterview;
      adjustedUrl = `interview/${businessId}/worksheet/${worksheetId}/${args.url}`;
    }
  }

  const adjustedArgs = { ...args, url: adjustedUrl };

  return baseQuery(adjustedArgs, api, extraOptions);
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: dynamicBaseQuery,
  keepUnusedDataFor: 0,
  endpoints: () => ({}),
  tagTypes: [
    'Comments',
    'Suppliers',
    'InterviewDetails',
    'Projects',
    'ProjectDetails',
    'Staff',
    'Companies',
    'InterviewList',
    'Categories',
    'AggregationList',
    'GeneralTranslations',
    'ProjectTranslations',
    'ProjectSetupState',
  ],
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
});
